import { forwardRef, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { IconButton } from './IconButton.jsx';
import { mergeRefs, useFocusTrap } from './useFocusTrap.js';

/**
 * A modal dialog (spec §7.2, §12): focus moves into the surface on open, is
 * trapped while open, and is restored to the invoker on close. Escape always
 * closes; clicking the scrim closes only when `dismissible !== false`
 * (use that for confirmation dialogs the user must resolve explicitly).
 * Rendered into a portal so its stacking context can never be trapped inside
 * a scrolling/overflow ancestor.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} [props.onClose]
 * @param {import('react').ReactNode} [props.title] - wired to `aria-labelledby`.
 * @param {import('react').ReactNode} [props.description] - wired to `aria-describedby`.
 * @param {import('react').ReactNode} props.children - body content.
 * @param {import('react').ReactNode} [props.footer]
 * @param {'default'|'wide'} [props.size='default']
 * @param {boolean} [props.dismissible=true] - `false` hides the close button
 *   and disables scrim-click dismissal (Escape still closes).
 * @param {string} [props.closeLabel='Close'] - accessible name for the built-in
 *   close button. ALWAYS pass a translated string (e.g. `t('common.close')`) —
 *   the default is an English fallback only, not localized product copy.
 * @param {import('react').RefObject<HTMLElement>} [props.initialFocusRef]
 * @param {string} [props.className]
 */
export const Dialog = forwardRef(function Dialog(
  {
    open,
    onClose,
    title,
    description,
    children,
    footer,
    size = 'default',
    dismissible = true,
    closeLabel = 'Close',
    initialFocusRef,
    className = '',
    ...rest
  },
  ref
) {
  const dialogRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();

  useFocusTrap({ active: open, containerRef: dialogRef, onClose, initialFocusRef });

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="etch-scrim"
        onClick={() => {
          if (dismissible !== false) onClose?.();
        }}
      />
      <div
        ref={mergeRefs(dialogRef, ref)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        data-size={size}
        className={`etch-dialog ${className}`.trim()}
        {...rest}
      >
        {(title || dismissible !== false) && (
          <div className="etch-dialog__header">
            {title && (
              <h2 id={titleId} className="etch-dialog__title">
                {title}
              </h2>
            )}
            {dismissible !== false && (
              <IconButton label={closeLabel} onClick={() => onClose?.()}>
                <X size={18} />
              </IconButton>
            )}
          </div>
        )}
        <div className="etch-dialog__body">
          {description && <p id={descriptionId}>{description}</p>}
          {children}
        </div>
        {footer && <div className="etch-dialog__footer">{footer}</div>}
      </div>
    </>,
    document.body
  );
});

export default Dialog;
